import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {InputGroup, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl, defineMessage} from 'react-intl'
import Autocomplete from 'react-google-autocomplete'
import {SearchIcon} from '../icons'
import {useLocaleCountry} from '../../hooks/use-locale-custom'

const ariaLabelMessages = {
    searchCityOrAddress: defineMessage({
        defaultMessage: 'Search city or address',
        id: 'service-center-locator.aria-label.search-city-or-address',
    }),
}
const useHidePoweredByGoogle = () => {
    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
        .pac-logo::after { display: none !important; }
        input.pac-target-input { width: 100% !important; outline: none !important; box-shadow: none !important; border-color: inherit !important; background: none !important; }
         .pac-container {
            z-index: 1500 !important;
            background: var(--chakra-colors-background-neutral-default);
            font-family: var(--chakra-fonts-body);
        }
            .pac-container .pac-logo.hdpi { }
        .pac-item { 
            color: var(--chakra-colors-foreground-neutral-subtle); 
            padding-left: 8px;
            padding-right: 8px;
            }
            .pac-item-query { 
                color: var(--chakra-colors-foreground-neutral-default); 
                font-size: 14px;
            }
            .pac-icon.pac-icon-marker { display: none !important;}
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])
}

const LocationSearchBar = ({onPlaceSelected}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ServiceCenterLocator')
    const currentCountry = useLocaleCountry().toUpperCase()
    const [googleReady, setGoogleReady] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [hasInput, setHasInput] = useState(false)

    useHidePoweredByGoogle()

    useEffect(() => {
        const interval = setInterval(() => {
            const input = document.querySelector('.pac-target-input')
            if (input) {
                input.setAttribute(
                    'aria-label',
                    intl.formatMessage(ariaLabelMessages.searchCityOrAddress),
                )
                const handler = (e) => {
                    setHasInput(!!e.target.value)
                }
                input.addEventListener('input', handler)
                setHasInput(!!input.value)
            }
            if (window.google && window.google.maps && window.google.maps.places) {
                setGoogleReady(true)
                clearInterval(interval)
            }
        }, 100)
        return () => clearInterval(interval)
    }, [googleReady])

    return (
        <>
            <InputGroup {...styles.locator} data-testid="autocomplete-input">
                <Text
                    {...styles.label}
                    sx={{
                        pointerEvents: 'none',
                        ...(isFocused || hasInput ? styles.labelActive : {}),
                    }}
                >
                    {intl.formatMessage({
                        defaultMessage: 'City',
                        id: 'use_address_fields.label.search_label',
                    })}
                </Text>
                {googleReady && (
                    <Autocomplete
                        apiKey={undefined}
                        onPlaceSelected={onPlaceSelected}
                        options={{
                            types: ['geocode'],
                            componentRestrictions: {
                                country: currentCountry.toLowerCase(),
                            },
                        }}
                        placeholder=""
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        aria-describedby="autocomplete-instructions"
                    />
                )}
                <SearchIcon />
            </InputGroup>

            {/* Hidden text for screen readers to provide instructions on how to use the autocomplete */}
            <Text id="autocomplete-instructions" style={{display: 'none'}}>
                {intl.formatMessage({
                    defaultMessage: 'Start typing and use the arrow keys to navigate suggestions.',
                    id: 'use_address_fields.autocomplete_instructions',
                })}
            </Text>
        </>
    )
}

LocationSearchBar.propTypes = {
    onPlaceSelected: PropTypes.func.isRequired,
}

export default LocationSearchBar
